const DEFAULT_THUMBNAIL_COUNT = 10;
const DEFAULT_CONTAINER_WIDTH = 800;
const DEFAULT_ASPECT_RATIO = 16 / 9;

const isPositiveFiniteNumber = (value: number | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const resolveThumbnailCount = (thumbnailCount?: number) =>
  Number.isInteger(thumbnailCount) && thumbnailCount !== undefined && thumbnailCount > 0
    ? thumbnailCount
    : DEFAULT_THUMBNAIL_COUNT;

export const resolveThumbnailAspectRatio = ({
  aspectRatio,
  videoWidth,
  videoHeight,
}: {
  aspectRatio?: number;
  videoWidth?: number;
  videoHeight?: number;
}) => {
  if (isPositiveFiniteNumber(aspectRatio)) return aspectRatio;

  if (isPositiveFiniteNumber(videoWidth) && isPositiveFiniteNumber(videoHeight)) {
    return videoWidth / videoHeight;
  }

  return DEFAULT_ASPECT_RATIO;
};

export const resolveThumbnailCanvasSize = ({
  aspectRatio,
  containerWidth,
  scaleRatio,
  thumbnailCount,
  videoWidth,
  videoHeight,
}: {
  aspectRatio?: number;
  containerWidth?: number;
  scaleRatio?: number;
  thumbnailCount?: number;
  videoWidth?: number;
  videoHeight?: number;
}) => {
  const safeThumbnailCount = resolveThumbnailCount(thumbnailCount);
  const safeContainerWidth = isPositiveFiniteNumber(containerWidth) ? containerWidth : DEFAULT_CONTAINER_WIDTH;
  const safeScaleRatio = isPositiveFiniteNumber(scaleRatio) ? scaleRatio : 1;
  const safeAspectRatio = resolveThumbnailAspectRatio({ aspectRatio, videoWidth, videoHeight });
  const width = Math.max(1, Math.round(safeScaleRatio * (safeContainerWidth / safeThumbnailCount)));
  const height = Math.max(1, Math.round(width / safeAspectRatio));

  return { width, height };
};

export const isValidThumbnailDataUrl = (imageUrl: string) =>
  imageUrl.startsWith('data:image/') && imageUrl !== 'data:,';

export const generateThumbnails = async ({
  videoSrc,
  containerRef,
  aspectRatio,
  quantity,
  onItemSuccess,
  onSuccess,
  thumbnailCount = DEFAULT_THUMBNAIL_COUNT,
  scaleRatio = 1,
}: {
  videoSrc: string;
  containerRef: React.RefObject<HTMLDivElement | null>;
  aspectRatio: number;
  quantity: number;
  onItemSuccess?: (index: number, image: string) => void;
  onSuccess?: (images: string[]) => void;
  thumbnailCount?: number;
  /** 缩放比例 */
  scaleRatio?: number;
}) => {
  try {
    if (!videoSrc.trim()) {
      onSuccess?.([]);
      return;
    }

    const tempVideo = document.createElement('video');
    tempVideo.crossOrigin = 'anonymous';
    tempVideo.src = videoSrc;
    await new Promise<void>((resolve) => {
      // 等待视频加载元据
      tempVideo.addEventListener('loadedmetadata', () => resolve(), { once: true });
      tempVideo.load();
    });
    const tempCanvas = document.createElement('canvas');
    const ctx = tempCanvas.getContext('2d');
    if (!ctx) return;
    const safeThumbnailCount = resolveThumbnailCount(thumbnailCount);
    const canvasSize = resolveThumbnailCanvasSize({
      aspectRatio,
      containerWidth: containerRef.current?.clientWidth,
      scaleRatio,
      thumbnailCount: safeThumbnailCount,
      videoWidth: tempVideo.videoWidth,
      videoHeight: tempVideo.videoHeight,
    });
    tempCanvas.width = canvasSize.width;
    tempCanvas.height = canvasSize.height;
    const duration = tempVideo.duration;
    if (!isPositiveFiniteNumber(duration)) return;
    const interval = duration / safeThumbnailCount;
    const newThumbnails: string[] = [];
    for (let i = 0; i < safeThumbnailCount; i++) {
      const timePoint = i === 0 ? 0.01 : i * interval; // 避免第一帧从 0 开始，Safari 可能不触发 seeked 事件
      tempVideo.currentTime = timePoint;
      await new Promise<void>((resolve) => {
        const seekHandler = () => {
          ctx.drawImage(tempVideo, 0, 0, tempCanvas.width, tempCanvas.height);
          const imageUrl = tempCanvas.toDataURL('image/webp', quantity);
          if (isValidThumbnailDataUrl(imageUrl)) {
            newThumbnails.push(imageUrl);
            onItemSuccess?.(i, imageUrl);
          }
          tempVideo.removeEventListener('seeked', seekHandler);
          resolve();
        };
        tempVideo.addEventListener('seeked', seekHandler, { once: true });
      });
    }
    // setVideoImages(newThumbnails);
    onSuccess?.(newThumbnails);
    // onSuccess;
  } catch (error) {
    console.error('生成视频缩略图失败:', error);
  }
};
